import { fetchWithAuth } from './fetchwithauth';

export type DashboardAccessSummary = {
  canAccessModule: boolean;
  canReadDashboard: boolean;
  effectivePermissions: string[];
};

export type WmsAccessSummary = {
  canAccessModule: boolean;
  canReadSourceInventory: boolean;
  canReadVarerobotInventory: boolean;
  canWriteVarerobotInventory: boolean;
  canRunSync: boolean;
  hasConfiguredVarerobot: boolean;
  configuredVarerobotProviderKey: string;
  effectivePermissions: string[];
  canReadBlueRobotInventory?: boolean;
  canWriteBlueRobotInventory?: boolean;
};

export type ProductCmsAccessSummary = {
  canAccessModule: boolean;
  canReadProducts: boolean;
  canWriteProducts: boolean;
  canRunBulkImport: boolean;
  effectivePermissions: string[];
};

export type ProductCmsAiAccessSummary = {
  canAccessModule: boolean;
  canViewWithinProductCmsSettings: boolean;
  canReadSettings: boolean;
  canWriteSettings: boolean;
  effectivePermissions: string[];
};

export type SeoCmsAccessSummary = {
  canAccessModule: boolean;
  canReadContent: boolean;
  canWriteContent: boolean;
  effectivePermissions: string[];
};

export type ProductImportAccessSummary = {
  canAccessModule: boolean;
  canRunBulkImport: boolean;
  effectivePermissions: string[];
};

export type LegacyModuleAccessSummary = { canAccessModule: boolean; effectivePermissions: string[] };

export type ModulesAccessResponse = {
  groupTreeRules?: LegacyModuleAccessSummary;
  posters?: LegacyModuleAccessSummary;
  productLocations?: LegacyModuleAccessSummary;
  pricing?: PricingAccessSummary;
  productImport?: ProductImportAccessSummary;
  dashboard: DashboardAccessSummary;
  wms: WmsAccessSummary;
  productCms: ProductCmsAccessSummary;
  productCmsAi: ProductCmsAiAccessSummary;
  seoCms: SeoCmsAccessSummary;
};

export type PricingAccessSummary = {
  canAccessModule: boolean;
  canReadPricing: boolean;
  canWriteDrafts: boolean;
  canPublish: boolean;
  effectivePermissions: string[];
};

export const defaultPricingAccess: PricingAccessSummary = {
  canAccessModule: false,
  canReadPricing: false,
  canWriteDrafts: false,
  canPublish: false,
  effectivePermissions: []
};

export const defaultDashboardAccess: DashboardAccessSummary = {
  canAccessModule: false,
  canReadDashboard: false,
  effectivePermissions: []
};

export const defaultWmsAccess: WmsAccessSummary = {
  canAccessModule: false,
  canReadSourceInventory: false,
  canReadVarerobotInventory: false,
  canWriteVarerobotInventory: false,
  canRunSync: false,
  hasConfiguredVarerobot: false,
  configuredVarerobotProviderKey: 'none',
  effectivePermissions: []
};

export const defaultProductCmsAccess: ProductCmsAccessSummary = {
  canAccessModule: false,
  canReadProducts: false,
  canWriteProducts: false,
  canRunBulkImport: false,
  effectivePermissions: []
};

export const defaultProductCmsAiAccess: ProductCmsAiAccessSummary = {
  canAccessModule: false,
  canViewWithinProductCmsSettings: false,
  canReadSettings: false,
  canWriteSettings: false,
  effectivePermissions: []
};

export const defaultSeoCmsAccess: SeoCmsAccessSummary = {
  canAccessModule: false,
  canReadContent: false,
  canWriteContent: false,
  effectivePermissions: []
};

export const defaultModulesAccess: ModulesAccessResponse = {
  posters: { canAccessModule: false, effectivePermissions: [] },
  productLocations: { canAccessModule: false, effectivePermissions: [] },
  pricing: defaultPricingAccess,
  productImport: { canAccessModule: false, canRunBulkImport: false, effectivePermissions: [] },
  dashboard: defaultDashboardAccess,
  wms: defaultWmsAccess,
  productCms: defaultProductCmsAccess,
  productCmsAi: defaultProductCmsAiAccess,
  seoCms: defaultSeoCmsAccess
};

export async function getModulesAccess(fetcher?: typeof fetch): Promise<ModulesAccessResponse> {
  const response = await fetchWithAuth('/api/modules/access', undefined, fetcher);

  if (!response.ok) {
    throw new Error(`Module access lookup failed: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as ModulesAccessResponse;

  return {
    groupTreeRules: { canAccessModule: Boolean(data.groupTreeRules?.canAccessModule), effectivePermissions: data.groupTreeRules?.effectivePermissions ?? [] },
    posters: { canAccessModule: Boolean(data.posters?.canAccessModule), effectivePermissions: data.posters?.effectivePermissions ?? [] },
    productLocations: { canAccessModule: Boolean(data.productLocations?.canAccessModule), effectivePermissions: data.productLocations?.effectivePermissions ?? [] },
    pricing: {
      canAccessModule: Boolean(data.pricing?.canAccessModule),
      canReadPricing: Boolean(data.pricing?.canAccessModule && data.pricing?.canReadPricing),
      canWriteDrafts: Boolean(data.pricing?.canAccessModule && data.pricing?.canWriteDrafts),
      canPublish: Boolean(data.pricing?.canAccessModule && data.pricing?.canPublish),
      effectivePermissions: Array.isArray(data.pricing?.effectivePermissions) ? data.pricing.effectivePermissions : []
    },
    productImport: {
      canAccessModule: Boolean(data.productImport?.canAccessModule),
      canRunBulkImport: Boolean(data.productImport?.canAccessModule && data.productImport?.canRunBulkImport),
      effectivePermissions: Array.isArray(data.productImport?.effectivePermissions) ? data.productImport.effectivePermissions : []
    },
    dashboard: {
      canAccessModule: Boolean(data.dashboard?.canAccessModule),
      canReadDashboard: Boolean(data.dashboard?.canReadDashboard),
      effectivePermissions: Array.isArray(data.dashboard?.effectivePermissions) ? data.dashboard.effectivePermissions : []
    },
    wms: {
      canAccessModule: Boolean(data.wms?.canAccessModule),
      canReadSourceInventory: Boolean(data.wms?.canReadSourceInventory),
      canReadVarerobotInventory: Boolean(data.wms?.canReadVarerobotInventory ?? data.wms?.canReadBlueRobotInventory),
      canWriteVarerobotInventory: Boolean(data.wms?.canWriteVarerobotInventory ?? data.wms?.canWriteBlueRobotInventory),
      canRunSync: Boolean(data.wms?.canRunSync),
      hasConfiguredVarerobot: Boolean(data.wms?.hasConfiguredVarerobot),
      configuredVarerobotProviderKey: data.wms?.configuredVarerobotProviderKey ?? 'none',
      effectivePermissions: Array.isArray(data.wms?.effectivePermissions) ? data.wms.effectivePermissions : []
    },
    productCms: {
      canAccessModule: Boolean(data.productCms?.canAccessModule),
      canReadProducts: Boolean(data.productCms?.canReadProducts),
      canWriteProducts: Boolean(data.productCms?.canWriteProducts),
      canRunBulkImport: Boolean(data.productCms?.canRunBulkImport),
      effectivePermissions: Array.isArray(data.productCms?.effectivePermissions) ? data.productCms.effectivePermissions : []
    },
    productCmsAi: {
      canAccessModule: Boolean(data.productCmsAi?.canAccessModule),
      canViewWithinProductCmsSettings: Boolean(data.productCmsAi?.canViewWithinProductCmsSettings),
      canReadSettings: Boolean(data.productCmsAi?.canReadSettings),
      canWriteSettings: Boolean(data.productCmsAi?.canWriteSettings),
      effectivePermissions: Array.isArray(data.productCmsAi?.effectivePermissions) ? data.productCmsAi.effectivePermissions : []
    },
    seoCms: {
      canAccessModule: Boolean(data.seoCms?.canAccessModule),
      canReadContent: Boolean(data.seoCms?.canReadContent),
      canWriteContent: Boolean(data.seoCms?.canWriteContent),
      effectivePermissions: Array.isArray(data.seoCms?.effectivePermissions) ? data.seoCms.effectivePermissions : []
    }
  };
}
